// Internal request-building helpers for the API client.
// Split out of client.ts to keep that file close to the §5.5 size target.

import type { AuthTokenProvider } from './auth';
import type { RequestInput } from './types';

function previewPrefix(): string {
  if (typeof window === 'undefined') return '';
  return window.location.pathname?.match(/^(\/preview\/pr-[1-9][0-9]*)(?:\/|$)/)?.[1] ?? '';
}

function requestPath(path: string): string {
  const prefix = previewPrefix();
  return prefix !== '' && (path === '/api' || path.startsWith('/api/')) ? `${prefix}${path}` : path;
}

function joinBaseAndPath(base: string, path: string): string {
  if (base.endsWith('/api') && (path === '/api' || path.startsWith('/api/'))) {
    return `${base}${path.slice('/api'.length)}`;
  }
  return `${base}${path}`;
}

export function buildRequestTarget(baseUrl: string, path: string): string {
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const rel = path.startsWith('/') ? path : `/${path}`;
  return base.length > 0 ? joinBaseAndPath(base, rel) : requestPath(rel);
}

export function buildUrl(
  baseUrl: string,
  path: string,
  query: RequestInput['query'],
): string {
  const target = buildRequestTarget(baseUrl, path);
  const origin = globalThis.location?.origin;
  const url = origin === undefined ? new URL(target) : new URL(target, origin);
  if (query !== undefined) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

export function buildHeaders(
  caller: Record<string, string> | undefined,
  auth: AuthTokenProvider,
  requestId: string,
  body: RequestInput['body'],
): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-Id': requestId,
    ...(caller ?? {}),
  };
  if (body !== undefined && !(body instanceof FormData) && headers['Content-Type'] === undefined) {
    headers['Content-Type'] = 'application/json';
  }
  const token = auth.getToken();
  if (token !== null && token.length > 0) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
